import { createContext, useContext, useState, useCallback } from 'react'
import ToastContainer from '../components/ToastContainer'

const ToastContext = createContext(null)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])

  const showToast = useCallback((toast) => {
    const id = Date.now() + Math.random()
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast,
    }
    setToasts((prev) => [...prev, newToast])
    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const success = useCallback(
    (message, options = {}) => {
      return showToast({
        type: 'success',
        message,
        title: options.title || 'Success',
        ...options,
      })
    },
    [showToast]
  )

  const error = useCallback(
    (message, options = {}) => {
      return showToast({
        type: 'error',
        message,
        title: options.title || 'Error',
        duration: 7000,
        ...options,
      })
    },
    [showToast]
  )

  const warning = useCallback(
    (message, options = {}) => {
      return showToast({
        type: 'warning',
        message,
        title: options.title || 'Warning',
        ...options,
      })
    },
    [showToast]
  )

  const info = useCallback(
    (message, options = {}) => {
      return showToast({
        type: 'info',
        message,
        title: options.title || 'Information',
        ...options,
      })
    },
    [showToast]
  )

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

